import type { DriverInfoDto } from "../../models/Delivery-Executive/DeliveryExecutive";
import type { OrderLocation } from "./InfoPopup";
import type { LatLng } from "./routeTypes";
import type { RouteForExport } from "./ExportRoutesExcel";
import type { DeliveryRoutePlanInput } from "../../models/delivery-planning";

export type EndpointDraft = { mode: "default" | "order" | "coordinates" | "location"; orderId: string; coordinates: string };
export type DriverRouteDraft = { driver: DriverInfoDto; vehicleNo: string; start: EndpointDraft; end: EndpointDraft };
export type DriverAssignment = { driver: DriverInfoDto; vehicleNo: string };
export type RouteMetrics = { driverId: number; distanceKm: number; drivingMinutes: number; orderCount: number };
export function estimateRouteMinutes(metrics: RouteMetrics[], deliveryMinutes: number, breakMinutes: number): number {
  // Directions measurements are whole seconds; minute inputs have one decimal.
  // Keep rounding identical to the API and avoid floating point ceil drift.
  return metrics.reduce((sum, route) => sum + Math.round(route.drivingMinutes * 60) +
    Math.round(route.orderCount * deliveryMinutes * 60) + Math.round(breakMinutes * 60), 0) / 60;
}
export function routeDistanceKm(metrics: RouteMetrics[]): number {
  return Math.round(metrics.reduce((sum, route) => sum + Math.round(route.distanceKm * 1000), 0) / 10) / 100;
}
export function buildDeliveryRoutePlan(routes: RouteForExport[], deliveryMinutes: number | null, breakMinutes: number | null): DeliveryRoutePlanInput {
  if (!routes.length || routes.length > 1000 || [deliveryMinutes, breakMinutes].some(n =>
    n === null || !Number.isFinite(n) || n < 0 || n > 1440 || Math.abs(n * 10 - Math.round(n * 10)) > 1e-8))
    throw new Error("Choose valid delivery and break times before saving.");
  const measurements = routes.map(route => {
    if (!Number.isFinite(route.totalDistanceKm) || !Number.isFinite(route.drivingMinutes) || route.totalDistanceKm! < 0 || route.drivingMinutes! < 0 ||
        !route.ordersInRoute.length || route.totalDistanceKm! > 100000 || route.drivingMinutes! > 525600)
      throw new Error("Route distance or driving time is unavailable. Rebuild the route before saving.");
    const orders = [...route.ordersInRoute].sort((a,b) => (a.sequence ?? 0) - (b.sequence ?? 0));
    if (orders.some((order, index) => !Number.isSafeInteger(Number(order.orderId)) || Number(order.orderId) <= 0 || order.sequence !== index + 1))
      throw new Error("The route sequence changed. Rebuild the route before saving.");
    return { distanceMeters: Math.round(route.totalDistanceKm! * 1000), drivingSeconds: Math.round(route.drivingMinutes! * 60),
      orderIds: orders.map(order => Number(order.orderId)) };
  });
  const ids = measurements.flatMap(route => route.orderIds);
  if (ids.length > 1000 || new Set(ids).size !== ids.length) throw new Error("Save routes with distinct orders.");
  return { deliveryMinutesPerOrder: deliveryMinutes!, breakMinutesPerRoute: breakMinutes!, routes: measurements };
}
export const defaultEndpoint = (): EndpointDraft => ({ mode: "default", orderId: "", coordinates: "" });

export function parseRoutePoint(value?: string | null): LatLng {
  const parts = value?.trim().split(/[,\s]+/).filter(Boolean) ?? [];
  const [lat, lng] = parts.map(Number);
  if (parts.length !== 2 || !Number.isFinite(lat) || !Number.isFinite(lng) || lat < -90 || lat > 90 || lng < -180 || lng > 180)
    throw new Error("Enter a valid latitude (-90 to 90) and longitude (-180 to 180).");
  return { lat, lng };
}

export function previewAssignments(drafts: DriverRouteDraft[], orders: OrderLocation[]): DriverAssignment[] {
  const point = (draft: EndpointDraft, fallback?: string | null) => {
    if (draft.mode === "default") return parseRoutePoint(fallback);
    if (draft.mode === "coordinates" || draft.mode === "location") return parseRoutePoint(draft.coordinates);
    const order = orders.find(o => String(o.orderId) === draft.orderId);
    if (!order) throw new Error("Choose an order still included in this route preview.");
    return parseRoutePoint(`${order.lat},${order.lng}`);
  };
  return drafts.map(d => {
    try {
      const start = point(d.start, d.driver.startLocation), end = point(d.end, d.driver.endLocation);
      return { vehicleNo: d.vehicleNo, driver: { ...d.driver, startLocation: `${start.lat},${start.lng}`, endLocation: `${end.lat},${end.lng}` } };
    } catch (e) { throw new Error(`${d.driver.name}: ${e instanceof Error ? e.message : "Check start and end points."}`); }
  });
}

export function removeEndpointOrder(drafts: DriverRouteDraft[], orderId: string): DriverRouteDraft[] {
  return drafts.map(d => ({ ...d,
    start: d.start.mode === "order" && d.start.orderId === orderId ? defaultEndpoint() : d.start,
    end: d.end.mode === "order" && d.end.orderId === orderId ? defaultEndpoint() : d.end,
  }));
}
